<script setup lang="ts">
import { computed } from "vue";

type SkeletonShape = "rect" | "circle" | "text";

const props = withDefaults(
  defineProps<{
    shape?: SkeletonShape;
    width?: string;
    height?: string;
    rounded?: "sm" | "md" | "full";
    lines?: number;
    animate?: boolean;
    // When inside a parent loading region (e.g. a wrapper that already carries role="status"),
    // pass `decorative` so this skeleton becomes aria-hidden and screen readers don't
    // announce duplicate "Loading" landmarks.
    decorative?: boolean;
  }>(),
  {
    shape: "rect",
    width: "100%",
    rounded: "md",
    lines: 1,
    animate: true,
    decorative: false,
  }
);

const defaultHeight = computed(() => (props.shape === "text" ? "1rem" : "20px"));
const resolvedHeight = computed(() => props.height ?? defaultHeight.value);

// Detect whether the value is a Tailwind class or a raw CSS value
const widthIsTailwind = computed(() => props.width.startsWith("w-"));
const heightIsTailwind = computed(() => resolvedHeight.value.startsWith("h-"));

const roundedClass = computed((): string => {
  if (props.shape === "circle") return "rounded-full";
  switch (props.rounded) {
    case "sm":
      return "rounded-4";
    case "full":
      return "rounded-full";
    default:
      return "rounded-8";
  }
});

// For circle shape, derive the height Tailwind class from the width class (w-8 → h-8)
const circleHeightClass = computed(() =>
  props.shape === "circle" && widthIsTailwind.value ? props.width.replace(/^w-/, "h-") : null
);

const blockClasses = computed(() => [
  "bg-slate-200",
  roundedClass.value,
  props.animate ? "animate-pulse" : null,
  widthIsTailwind.value ? props.width : null,
  props.shape === "circle" ? circleHeightClass.value : heightIsTailwind.value ? resolvedHeight.value : null,
]);

const blockStyle = computed((): Record<string, string> | undefined => {
  const styles: Record<string, string> = {};
  if (!widthIsTailwind.value) styles.width = props.width;
  if (props.shape === "circle") {
    // Square: height equals width
    if (!widthIsTailwind.value) styles.height = props.width;
  } else if (!heightIsTailwind.value) {
    styles.height = resolvedHeight.value;
  }
  return Object.keys(styles).length ? styles : undefined;
});

const isMultiLine = computed(() => props.shape === "text" && props.lines > 1);

// For multi-line text: last line renders at 60% width for a natural look
function lineClasses(lineIndex: number): (string | null)[] {
  const isLast = lineIndex === props.lines;
  return [
    "block bg-slate-200",
    roundedClass.value,
    props.animate ? "animate-pulse" : null,
    !isLast && widthIsTailwind.value ? props.width : null,
    heightIsTailwind.value ? resolvedHeight.value : null,
  ];
}

function lineStyle(lineIndex: number): Record<string, string> {
  const isLast = lineIndex === props.lines;
  const styles: Record<string, string> = {};
  if (!heightIsTailwind.value) styles.height = resolvedHeight.value;
  if (isLast) {
    styles.width = "60%";
  } else if (!widthIsTailwind.value) {
    styles.width = props.width;
  }
  return styles;
}
</script>

<template>
  <!-- Multi-line text skeleton -->
  <div
    v-if="isMultiLine"
    :role="decorative ? undefined : 'status'"
    :aria-label="decorative ? undefined : 'Loading'"
    :aria-hidden="decorative ? 'true' : undefined"
    class="gap-px-8 flex flex-col">
    <span
      v-for="lineIndex in lines"
      :key="lineIndex"
      :class="lineClasses(lineIndex)"
      :style="lineStyle(lineIndex)" />
  </div>

  <!-- Single block (rect, circle, or single text line) -->
  <div
    v-else
    :role="decorative ? undefined : 'status'"
    :aria-label="decorative ? undefined : 'Loading'"
    :aria-hidden="decorative ? 'true' : undefined"
    :class="blockClasses"
    :style="blockStyle" />
</template>
